/**
 * สถานะการนำทางภายในหน้าจอรับเข้าสต๊อก และป้ายกำกับที่ใช้ร่วมกัน
 *
 * ทำไมใช้ query string ไม่ใช่ path ย่อย: `App.tsx` ประกาศ route ของทุกโมดูลจาก
 * `DESKTOP_MODULE_ROUTES` เส้นละหนึ่ง path แบบตรงตัว และ Work Order กำหนดว่า
 * หน้าจอนี้ใช้ path ที่ลงทะเบียนไว้แล้วเส้นเดียว (`/inventory/goods-receipt`)
 * ห้ามคิด path ที่สองขึ้นมาเอง — เหตุผลเดียวกับ `productionOrders/productionOrderView.ts`
 */

import {
  hasCrossPlantScope,
  selectablePlants,
} from '../productionOrders/productionOrderView';
import type { StatusTone } from '../StatusBadge';
import {
  QUALITY_BLOCKED,
  QUALITY_HOLD,
  QUALITY_REJECTED,
  QUALITY_RELEASED,
} from '../../lib/goodsReceipts/types';

/**
 * ขอบเขตโรงงานของผู้ใช้ — ใช้ฟังก์ชันเดียวกับหน้าจอใบสั่งผลิต ไม่ได้เขียนใหม่
 *
 * ทั้งสองตัวตอบคำถามเดียวกันเป๊ะ ("`plant_scope` ที่เซิร์ฟเวอร์ส่งมาแปลว่าเลือก
 * โรงงานไหนได้บ้าง" ตาม D-004) และไม่มีอะไรเกี่ยวกับใบสั่งผลิตอยู่ในตัวมันเลย
 * การคัดลอกมาไว้ที่นี่จะได้กฎเดียวกันสองชุดที่วันหนึ่งจะเลื่อนออกจากกัน — ซึ่งเป็น
 * ความผิดพลาดแบบเดียวกับที่ `lib/businessTime.ts` ถูกสร้างขึ้นมาเพื่อลบทิ้ง
 *
 * ⚠️ ที่ยังไม่ยกขึ้นไปเป็น helper กลาง เพราะ `desktop/productionOrders/**` และ
 * ที่ที่ควรจะเป็นบ้านของมันอยู่นอก write scope ของ Work Order นี้ — เป็นข้อเสนอ
 * ให้ `erp-orchestrator` ไม่ใช่การตัดสินใจของหน้าจอนี้
 */
export { hasCrossPlantScope, selectablePlants };

export const RECEIPT_ID_PARAM = 'receipt_id';
export const VIEW_PARAM = 'view';
export const CREATE_VIEW_VALUE = 'new';

export type GoodsReceiptView =
  | { readonly kind: 'list' }
  | { readonly kind: 'detail'; readonly receiptId: string }
  | { readonly kind: 'create' };

/**
 * แปลง query เป็นหน้าจอที่ต้องแสดง
 *
 * `receipt_id` มาก่อน `view=new` เสมอ เพื่อให้ URL ที่หลงเหลือทั้งสองค่า (เช่นหลัง
 * รับเข้าสำเร็จแล้วผู้ใช้กด back/forward) จบลงที่ใบที่มีอยู่จริง ไม่ใช่ฟอร์มเปล่าที่ดู
 * เหมือนของยังไม่ถูกรับเข้า — ซึ่งชวนให้กรอกซ้ำแล้วรับของกองเดิมเข้าสองครั้ง
 */
export function resolveGoodsReceiptView(params: URLSearchParams): GoodsReceiptView {
  const receiptId = params.get(RECEIPT_ID_PARAM)?.trim() ?? '';
  if (receiptId !== '') {
    return { kind: 'detail', receiptId };
  }
  if (params.get(VIEW_PARAM) === CREATE_VIEW_VALUE) {
    return { kind: 'create' };
  }
  return { kind: 'list' };
}

export function receiptDetailSearch(receiptId: string): string {
  return `?${RECEIPT_ID_PARAM}=${encodeURIComponent(receiptId)}`;
}

export function receiptCreateSearch(): string {
  return `?${VIEW_PARAM}=${CREATE_VIEW_VALUE}`;
}

/** กลับไปหน้ารายการ = path เดิมที่ไม่มี query เลย */
export function receiptListSearch(): string {
  return '';
}

/* ------------------------------------------------------------- ป้ายกำกับ -- */

/**
 * ป้ายสถานะคุณภาพของ lot — **ต้องอ่านออกว่าเบิกไปใช้ได้หรือยัง**
 *
 * `QUALITY_HOLD` ไม่ได้เขียนว่า "กักรอตรวจ" เฉย ๆ เพราะคำนั้นอ่านเหมือนขั้นตอน
 * เอกสาร: สิ่งที่พนักงานคลังต้องรู้คือของกองนี้ **ยังเบิกไปใช้ไม่ได้** และจะได้ก็
 * ต่อเมื่อมีคนอื่นตัดสินปล่อยผ่าน (D-059)
 *
 * สถานะที่ไม่รู้จักคืนรหัสดิบ ไม่ใช่ "ไม่ทราบ" — ถ้าเซิร์ฟเวอร์เพิ่มสถานะใหม่
 * ผู้ใช้ควรเห็นรหัสจริงเพื่อแจ้งต่อได้ ดีกว่าเห็นคำที่ไม่มีความหมาย
 */
export function qualityStatusLabel(status: string): string {
  switch (status) {
    case QUALITY_HOLD:
      return 'กักรอตรวจ — ยังเบิกไปใช้ไม่ได้';
    case QUALITY_RELEASED:
      return 'ปล่อยผ่านแล้ว — เบิกไปใช้ได้';
    case QUALITY_REJECTED:
      return 'ไม่ผ่าน — เบิกไปใช้ไม่ได้';
    case QUALITY_BLOCKED:
      return 'ถูกระงับ — เบิกไปใช้ไม่ได้';
    default:
      return status;
  }
}

/**
 * โทนของป้าย — **`QUALITY_HOLD` ไม่ใช่ `success`**
 *
 * การ post สำเร็จกับของที่ใช้ได้เป็นคนละเรื่อง ป้ายสีเขียวบนของที่ยังถูกกักคือ
 * หน้าจอที่บอกว่าเรียบร้อยแล้วทั้งที่ยังเบิกไม่ได้
 */
export function qualityStatusTone(status: string): StatusTone {
  switch (status) {
    case QUALITY_HOLD:
      return 'warning';
    case QUALITY_RELEASED:
      return 'success';
    case QUALITY_REJECTED:
    case QUALITY_BLOCKED:
      return 'danger';
    default:
      return 'neutral';
  }
}
